import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { LockIndicator } from '../components/permissions/LockIndicator';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Users, Plus, Eye, Trash2, Loader2, Search, Phone, Mail, User, Star, PhoneCall, PhoneForwarded, Bell, Clock, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ProspectFormDialog } from '../components/prospects/ProspectFormDialog';
import { ProspectDetailDialog } from '../components/prospects/ProspectDetailDialog';
import { StatusHistoryDialog } from '../components/prospects/StatusHistoryDialog';
import { VisiteFormDialog } from '../components/visites/VisiteFormDialog';
import { ConfirmationRDVDialog } from '../components/prospects/ConfirmationRDVDialog';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    sans_reponse_1: 'bg-orange-100 text-orange-700 border-orange-200',
    sans_reponse_2: 'bg-orange-100 text-orange-700 border-orange-200',
    sans_reponse_3: 'bg-orange-100 text-orange-700 border-orange-200',
    a_rappeler: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    devis_estimatif: 'bg-violet-100 text-violet-700 border-violet-200',
    listing_en_attente: 'bg-slate-100 text-slate-700 border-slate-200',
    listing_telephone: 'bg-violet-100 text-violet-700 border-violet-200',
    listing_email: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    visite: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    faux_contact: 'bg-red-100 text-red-700 border-red-200',
    non_interessant: 'bg-red-100 text-red-600 border-red-200'
};

function hoursSince(dateStr) {
    if (!dateStr) return 0;
    try {
        const date = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
        return (new Date() - date) / (1000 * 60 * 60);
    } catch {
        return 0;
    }
}

function timeAgo(dateStr) {
    if (!dateStr) return '-';
    try {
        const now = new Date();
        const date = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        const diffWeek = Math.floor(diffDay / 7);
        const diffMonth = Math.floor(diffDay / 30);

        if (diffSec < 60) return "à l'instant";
        if (diffMin < 60) return `il y a ${diffMin} min`;
        if (diffHour < 24) return `il y a ${diffHour}h`;
        if (diffDay < 7) return `il y a ${diffDay}j`;
        if (diffWeek < 5) return `il y a ${diffWeek} sem.`;
        if (diffMonth < 12) return `il y a ${diffMonth} mois`;
        return `il y a ${Math.floor(diffDay / 365)} an(s)`;
    } catch {
        return '-';
    }
}

export function ProspectsPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [prospects, setProspects] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyProspect, setHistoryProspect] = useState(null);
    const [isVisiteFormOpen, setIsVisiteFormOpen] = useState(false);
    const [visiteProspect, setVisiteProspect] = useState(null);
    const [prefillAdresse, setPrefillAdresse] = useState(null);
    const [showRappelModal, setShowRappelModal] = useState(false);
    const [rappelProspect, setRappelProspect] = useState(null);
    const [rappelRemarque, setRappelRemarque] = useState('');
    const [rappelSaving, setRappelSaving] = useState(false);
    // Listing en attente modal
    const [showListingModal, setShowListingModal] = useState(false);
    const [listingProspect, setListingProspect] = useState(null);
    const [listingRemarque, setListingRemarque] = useState('');
    const [listingSaving, setListingSaving] = useState(false);
    // Rappel cards state
    const [rappelStats, setRappelStats] = useState({ sans_reponse_1: 0, sans_reponse_2: 0, a_rappeler: 0, attente_listing: 0 });
    const [activeRappelView, setActiveRappelView] = useState(null); // null | 'sr1' | 'sr2' | 'ar' | 'al'
    const [rappelList, setRappelList] = useState([]);
    const [rappelListLoading, setRappelListLoading] = useState(false);
    // Confirmation RDV post-création (flow "Planifier visite")
    const [confirmationRdvOpen, setConfirmationRdvOpen] = useState(false);
    const [confirmationRdvProspect, setConfirmationRdvProspect] = useState(null);
    const [confirmationRdvVisite, setConfirmationRdvVisite] = useState(null);

    const fetchRappelStats = async () => {
        try {
            const { data } = await axios.get(`${API}/api/prospects/rappels-stats`, { withCredentials: true });
            setRappelStats(data);
        } catch {
            // silent
        }
    };

    const refreshActiveRappelList = async () => {
        if (!activeRappelView) return;
        const endpointMap = {
            sr1: 'sans-reponse-1',
            sr2: 'sans-reponse-2',
            ar: 'a-rappeler',
            al: 'attente-listing',
        };
        try {
            const { data } = await axios.get(`${API}/api/prospects/rappels/${endpointMap[activeRappelView]}`, { withCredentials: true });
            setRappelList(data);
        } catch { /* silent */ }
    };

    const openRappelView = async (viewKey) => {
        // Toggle off if clicking the active card
        if (activeRappelView === viewKey) {
            setActiveRappelView(null);
            setRappelList([]);
            return;
        }
        setActiveRappelView(viewKey);
        setRappelListLoading(true);
        const endpointMap = {
            sr1: 'sans-reponse-1',
            sr2: 'sans-reponse-2',
            ar: 'a-rappeler',
            al: 'attente-listing',
        };
        try {
            const { data } = await axios.get(`${API}/api/prospects/rappels/${endpointMap[viewKey]}`, { withCredentials: true });
            setRappelList(data);
        } catch {
            toast.error('Erreur lors du chargement');
            setRappelList([]);
        } finally {
            setRappelListLoading(false);
        }
    };

    // Auto-open new prospect form when ?action=nouveau
    useEffect(() => {
        if (searchParams.get('action') === 'nouveau') {
            setSelectedProspect(null);
            setPrefillAdresse(location.state?.adresse_depart || null);
            setIsFormOpen(true);
            setSearchParams({}, { replace: true });
            // Clear location state
            window.history.replaceState({}, '');
        }
    }, [searchParams, setSearchParams, location.state]);

    const fetchConfig = async () => {
        try {
            const { data } = await axios.get(`${API}/api/prospects/config`, { withCredentials: true });
            setConfig(data);
        } catch (error) {
            console.error('Error fetching config:', error);
        }
    };

    const fetchProspects = async (silent = false) => {
        try {
            let url = `${API}/api/prospects`;
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (statusFilter && statusFilter !== 'all') params.append('statut', statusFilter);
            params.append('page', String(page));
            params.append('page_size', '50');
            url += `?${params.toString()}`;

            const { data } = await axios.get(url, { withCredentials: true });
            setProspects(prev => listsEqualById(prev, data.items) ? prev : data.items);
            setTotal(data.total);
            setTotalPages(data.pages);
        } catch (error) {
            if (!silent) toast.error('Erreur lors du chargement des prospects');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        fetchConfig();
        fetchRappelStats();
    }, []);

    // Recherche / filtre statut -> retour page 1
    useEffect(() => {
        if (page !== 1) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, statusFilter]);

    useEffect(() => {
        fetchProspects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, statusFilter, page]);

    useAutoRefresh(() => fetchProspects(true));

    const prospectLocks = useBulkLocks('prospect', prospects.map(p => p.id));

    const handleDelete = async () => {
        if (!selectedProspect) return;
        setIsDeleting(true);
        try {
            await axios.delete(`${API}/api/prospects/${selectedProspect.id}`, { withCredentials: true });
            toast.success('Prospect supprimé avec succès');
            setIsDeleteOpen(false);
            setSelectedProspect(null);
            fetchProspects();
            fetchRappelStats();
            refreshActiveRappelList();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setIsDeleting(false);
        }
    };

    const [editFromDetail, setEditFromDetail] = useState(false);

    const openDetail = (prospect) => {
        setSelectedProspect(prospect);
        setIsDetailOpen(true);
    };

    const openDelete = (prospect) => {
        setSelectedProspect(prospect);
        setIsDeleteOpen(true);
    };

    const openEdit = (prospect) => {
        setSelectedProspect(prospect);
        setIsFormOpen(true);
    };

    const openEditFromDetail = (prospect) => {
        setIsDetailOpen(false);
        setEditFromDetail(true);
        openEdit(prospect);
    };

    const handleFormClose = async (saved) => {
        if (editFromDetail && selectedProspect) {
            // Switch directly from form to detail without showing list
            setIsFormOpen(false);
            setIsDetailOpen(true);
            setEditFromDetail(false);
            // Refresh data in background
            fetchProspects();
            try {
                const { data } = await axios.get(`${API}/api/prospects/${selectedProspect.id}`, { withCredentials: true });
                setSelectedProspect(data);
            } catch (error) {
                console.error('Error refreshing prospect:', error);
            }
        } else {
            setIsFormOpen(false);
            setSelectedProspect(null);
            if (saved) fetchProspects();
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    const getStatusLabel = (status) => {
        return config?.statuses?.find(s => s.value === status)?.label || status;
    };

    const getSourceLabel = (source) => {
        return config?.sources?.find(s => s.value === source)?.label || source || '-';
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="prospects-page">
            <ReadOnlyGuard module="prospects">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    {activeRappelView && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setActiveRappelView(null); setRappelList([]); }}
                            data-testid="rappel-back-to-prospects-btn"
                        >
                            <ArrowLeft size={16} className="mr-1.5" />
                            Retour
                        </Button>
                    )}
                    <div>
                        <h1 
                            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="prospects-title"
                        >
                            Prospects
                        </h1>
                        <p className="mt-1 text-slate-500">
                            Gérez vos prospects et opportunités
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => {
                        setSelectedProspect(null);
                        setIsFormOpen(true);
                    }}
                    className="bg-[#2e7cd6] hover:bg-blue-700 text-white"
                    data-testid="add-prospect-button"
                >
                    <Plus size={20} className="mr-2" />
                    Nouveau Prospect
                </Button>
            </div>

            {/* Rappel Stat Cards (style du Devis) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="prospect-rappel-stats">
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeRappelView === 'sr1' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-orange-200'}`}
                    onClick={() => openRappelView('sr1')}
                    data-testid="rappel-card-sr1"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                            <PhoneCall size={20} className="text-orange-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-orange-700">{rappelStats.sans_reponse_1}</p>
                            <p className="text-xs text-slate-500">Sans réponse 1 · à rappeler (2e fois)</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeRappelView === 'sr2' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'}`}
                    onClick={() => openRappelView('sr2')}
                    data-testid="rappel-card-sr2"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                            <PhoneForwarded size={20} className="text-red-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-red-700">{rappelStats.sans_reponse_2}</p>
                            <p className="text-xs text-slate-500">Sans réponse 2 · à rappeler (3e fois)</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeRappelView === 'ar' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-200'}`}
                    onClick={() => openRappelView('ar')}
                    data-testid="rappel-card-ar"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Bell size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-amber-700">{rappelStats.a_rappeler}</p>
                            <p className="text-xs text-slate-500">Demande de rappel</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className={`rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white ${activeRappelView === 'al' ? 'border-slate-500 ring-2 ring-slate-200' : 'border-slate-200'}`}
                    onClick={() => openRappelView('al')}
                    data-testid="rappel-card-al"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                            <Clock size={20} className="text-slate-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-slate-700">{rappelStats.attente_listing}</p>
                            <p className="text-xs text-slate-500">Attente listing</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters — masqués quand une vue rappel est active */}
            {!activeRappelView && (
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Rechercher (nom, téléphone, email)..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {config?.statuses?.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                        {status.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Prospects Table */}
            {!activeRappelView && (
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Users size={20} className="text-[#2e7cd6]" />
                        Liste des prospects ({total})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : prospects.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun prospect trouvé
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="prospects-table" className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Date de création</TableHead>
                                        <TableHead>Source</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Modifié</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {prospects.map((prospect) => (
                                        <TableRow key={prospect.id} data-testid={`prospect-row-${prospect.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(prospect)}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                        <User size={20} className="text-slate-500" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900 flex items-center gap-2">
                                                            {prospect.client_nom}
                                                            <LockIndicator lock={prospectLocks[prospect.id]} />
                                                        </p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="w-10">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setHistoryProspect(prospect);
                                                        setIsHistoryOpen(true);
                                                    }}
                                                    className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                    title="Historique des statuts"
                                                    data-testid={`status-history-${prospect.id}`}
                                                >
                                                    <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                                                </button>
                                            </TableCell>
                                            <TableCell className="text-slate-600">
                                                {formatDate(prospect.created_at)}
                                            </TableCell>
                                            <TableCell className="text-slate-600">
                                                {getSourceLabel(prospect.source)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline" 
                                                    className={`${STATUS_COLORS[prospect.statut] || 'bg-slate-100 text-slate-700'} border`}
                                                >
                                                    {getStatusLabel(prospect.statut)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-slate-500 text-sm">
                                                {timeAgo(prospect.status_changed_at || prospect.created_at)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={(e) => { e.stopPropagation(); openDetail(prospect); }}
                                                        data-testid={`view-prospect-${prospect.id}`}
                                                    >
                                                        <Eye size={16} className="text-slate-600" />
                                                    </Button>
                                                    {user?.role === 'admin' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={(e) => { e.stopPropagation(); openDelete(prospect); }}
                                                            data-testid={`delete-prospect-${prospect.id}`}
                                                            title="Supprimer (admin)"
                                                        >
                                                            <Trash2 size={16} className="text-red-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    {!loading && total > 0 && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 border-t border-slate-200" data-testid="prospects-pagination">
                            <span className="text-xs text-slate-500">
                                {total} prospect{total > 1 ? 's' : ''} — page {page} sur {totalPages}
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    data-testid="prospects-pagination-prev"
                                >
                                    <ChevronLeft size={14} className="mr-1" /> Précédent
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    data-testid="prospects-pagination-next"
                                >
                                    Suivant <ChevronRight size={14} className="ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Rappel filtered list */}
            {activeRappelView && (
                <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="rappel-list-card">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                            {activeRappelView === 'sr1' && <><PhoneCall size={20} className="text-orange-500" /> Sans réponse 1 — à rappeler (2e fois)</>}
                            {activeRappelView === 'sr2' && <><PhoneForwarded size={20} className="text-red-500" /> Sans réponse 2 — à rappeler (3e fois)</>}
                            {activeRappelView === 'ar' && <><Bell size={20} className="text-amber-500" /> Demande de rappel</>}
                            {activeRappelView === 'al' && <><Clock size={20} className="text-slate-500" /> Attente listing</>}
                            <span className="text-slate-400 font-normal">({rappelList.length})</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 sm:p-6">
                        {rappelListLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                            </div>
                        ) : rappelList.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">Aucun élément dans cette liste</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table className="min-w-[700px]" data-testid="rappel-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Client</TableHead>
                                            {activeRappelView !== 'al' && <TableHead>Tâche</TableHead>}
                                            {(activeRappelView === 'ar' || activeRappelView === 'al') && <TableHead>Remarque</TableHead>}
                                            <TableHead>Depuis</TableHead>
                                            <TableHead className="text-right">Action</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(() => {
                                            // Urgency thresholds (hours): SR1/SR2 = 24h, AL = 48h, AR = none
                                            const urgencyHours = (activeRappelView === 'sr1' || activeRappelView === 'sr2') ? 24
                                                : activeRappelView === 'al' ? 48
                                                : null;
                                            // Sort: urgent first (if applicable), then by oldest first (largest hoursSince)
                                            const sorted = [...rappelList].sort((a, b) => {
                                                const ha = hoursSince(a.status_changed_at);
                                                const hb = hoursSince(b.status_changed_at);
                                                if (urgencyHours !== null) {
                                                    const ua = ha >= urgencyHours;
                                                    const ub = hb >= urgencyHours;
                                                    if (ua !== ub) return ua ? -1 : 1;
                                                }
                                                return hb - ha;
                                            });
                                            return sorted.map(item => {
                                            const hours = hoursSince(item.status_changed_at);
                                            const isUrgent = urgencyHours !== null && hours >= urgencyHours;
                                            const taskLabel =
                                                activeRappelView === 'sr1' ? 'Rappeler pour la 2e fois' :
                                                activeRappelView === 'sr2' ? 'Rappeler pour la 3e fois' :
                                                activeRappelView === 'ar' ? 'Rappeler le client' :
                                                '';
                                            const handleOpenDetail = async () => {
                                                try {
                                                    const { data } = await axios.get(`${API}/api/prospects/${item.id}`, { withCredentials: true });
                                                    openDetail(data);
                                                } catch {
                                                    toast.error('Erreur lors du chargement du prospect');
                                                }
                                            };
                                            return (
                                                <TableRow key={item.id} className={`cursor-pointer hover:bg-slate-50 ${isUrgent ? 'bg-red-50/60 hover:bg-red-50' : ''}`} onClick={handleOpenDetail} data-testid={`rappel-row-${item.id}`}>
                                                    <TableCell>
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isUrgent ? 'bg-red-100' : 'bg-slate-100'}`}>
                                                                <User size={20} className={isUrgent ? 'text-red-600' : 'text-slate-500'} />
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-medium text-slate-900">{item.client_nom}</p>
                                                                {isUrgent && (
                                                                    <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 uppercase text-[10px] font-bold tracking-wide" data-testid={`rappel-urgent-${item.id}`}>
                                                                        Urgent
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    {activeRappelView !== 'al' && (
                                                        <TableCell>
                                                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                                {taskLabel}
                                                            </Badge>
                                                        </TableCell>
                                                    )}
                                                    {(activeRappelView === 'ar' || activeRappelView === 'al') && (
                                                        <TableCell className="text-slate-600 text-sm max-w-md">
                                                            {item.remarque ? (
                                                                <span className="italic">"{item.remarque}"</span>
                                                            ) : (
                                                                <span className="text-slate-400">—</span>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell className={`text-sm ${isUrgent ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                                                        {timeAgo(item.status_changed_at)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={(e) => { e.stopPropagation(); handleOpenDetail(); }}
                                                            data-testid={`rappel-view-${item.id}`}
                                                            className="gap-1.5"
                                                        >
                                                            <Eye size={14} />
                                                            Aperçu
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                        })()}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Form Dialog */}
            {config && (
                <ProspectFormDialog
                    open={isFormOpen}
                    onOpenChange={(open) => {
                        if (!open) { handleFormClose(false); setPrefillAdresse(null); }
                    }}
                    prospect={selectedProspect}
                    config={config}
                    prefillAdresseDepart={prefillAdresse}
                    onSuccess={async (newProspect) => {
                        if (newProspect && sessionStorage.getItem('visite_prefill')) {
                            // Auto-create prospect + visite from "Programmation visite"
                            try {
                                const prefill = JSON.parse(sessionStorage.getItem('visite_prefill'));
                                sessionStorage.removeItem('visite_prefill');
                                // 1. Create visite FIRST — si ça échoue, on ne change pas le statut
                                //    (sinon le prospect reste bloqué en "visite" sans visite associée)
                                await axios.post(`${API}/api/visites`, {
                                    prospect_id: newProspect.id,
                                    date: prefill.date,
                                    heure: prefill.heure,
                                    type_visite: prefill.type_visite,
                                    commercial_id: ''
                                }, { withCredentials: true });
                                // 2. Visite OK → change status to "visite"
                                await axios.patch(`${API}/api/prospects/${newProspect.id}/status?statut=visite`, {}, { withCredentials: true });
                                toast.success('Prospect créé et visite programmée avec succès');
                                setIsFormOpen(false);
                                setPrefillAdresse(null);
                                fetchProspects();
                                // 3. Ouvrir le dialog de confirmation RDV (email optionnel)
                                setConfirmationRdvProspect(newProspect);
                                setConfirmationRdvVisite({
                                    date: prefill.date,
                                    heure: prefill.heure,
                                    type_visite: prefill.type_visite,
                                });
                                setConfirmationRdvOpen(true);
                            } catch {
                                toast.error('Prospect créé mais erreur lors de la programmation de la visite');
                                handleFormClose(true);
                                setPrefillAdresse(null);
                            }
                        } else {
                            handleFormClose(true);
                            setPrefillAdresse(null);
                        }
                    }}
                />
            )}

            {/* Confirmation RDV Dialog (post-création, uniquement depuis Planifier une visite) */}
            <ConfirmationRDVDialog
                open={confirmationRdvOpen}
                onOpenChange={setConfirmationRdvOpen}
                prospect={confirmationRdvProspect}
                visite={confirmationRdvVisite}
                onDone={() => {
                    setConfirmationRdvProspect(null);
                    setConfirmationRdvVisite(null);
                }}
            />

            {/* Detail Dialog */}
            {config && selectedProspect && (
                <ProspectDetailDialog
                    open={isDetailOpen}
                    onOpenChange={setIsDetailOpen}
                    prospect={selectedProspect}
                    config={config}
                    onEdit={() => {
                        openEditFromDetail(selectedProspect);
                    }}
                    onRefresh={async () => {
                        await fetchProspects();
                        fetchRappelStats();
                        refreshActiveRappelList();
                        // Refresh selected prospect data
                        try {
                            const { data } = await axios.get(`${API}/api/prospects/${selectedProspect.id}`, { withCredentials: true });
                            setSelectedProspect(data);
                        } catch (error) {
                            console.error('Error refreshing prospect:', error);
                        }
                    }}
                    onStatusVisite={(prospect) => {
                        setIsDetailOpen(false);
                        setTimeout(() => {
                            setVisiteProspect(prospect);
                            setIsVisiteFormOpen(true);
                        }, 200);
                    }}
                    onRappelRequest={(prospect) => {
                        setIsDetailOpen(false);
                        setTimeout(() => {
                            setRappelProspect(prospect);
                            setRappelRemarque('');
                            setShowRappelModal(true);
                        }, 200);
                    }}
                    onListingEnAttenteRequest={(prospect) => {
                        setIsDetailOpen(false);
                        setTimeout(() => {
                            setListingProspect(prospect);
                            setListingRemarque('');
                            setShowListingModal(true);
                        }, 200);
                    }}
                />
            )}

            {/* Visite Form Dialog */}
            <VisiteFormDialog
                open={isVisiteFormOpen}
                onOpenChange={(val) => {
                    setIsVisiteFormOpen(val);
                    if (!val) {
                        setVisiteProspect(null);
                    }
                }}
                prospect={visiteProspect}
                onSuccess={async () => {
                    // Visite confirmée → changer le statut en "visite"
                    if (visiteProspect) {
                        try {
                            await axios.patch(
                                `${API}/api/prospects/${visiteProspect.id}/status?statut=visite`,
                                {},
                                { withCredentials: true }
                            );
                        } catch (e) {
                            console.error('Error updating prospect status:', e);
                        }
                    }
                    setIsVisiteFormOpen(false);
                    setVisiteProspect(null);
                    fetchProspects();
                    fetchRappelStats();
                    refreshActiveRappelList();
                }}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent data-testid="delete-prospect-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer le prospect <strong>{selectedProspect?.client_nom}</strong> ? 
                            Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-prospect"
                        >
                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Status History Dialog */}
            <StatusHistoryDialog
                open={isHistoryOpen}
                onOpenChange={setIsHistoryOpen}
                prospect={historyProspect}
            />

            {/* Modal Rappel - en dehors de tout Dialog Radix */}
            {showRappelModal && rappelProspect && (
                <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="rappel-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Prospect à rappeler — {rappelProspect.client_nom}
                            </h2>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-500">Une tâche sera créée automatiquement pour l'assistante.</p>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Remarque importante pour le rappel</label>
                                <textarea
                                    value={rappelRemarque}
                                    onChange={e => setRappelRemarque(e.target.value)}
                                    rows={3}
                                    className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2e7cd6] focus:border-transparent resize-none"
                                    placeholder="Notez une remarque importante pour rappeler le client qui demande à être recontacté (horaire préféré, sujet, contexte...)"
                                    autoFocus
                                    data-testid="rappel-remarque"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                            <Button variant="outline" size="sm" onClick={() => { setShowRappelModal(false); setRappelProspect(null); }}>Annuler</Button>
                            <Button
                                size="sm"
                                className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                disabled={rappelSaving}
                                data-testid="rappel-confirm-btn"
                                onClick={async () => {
                                    setRappelSaving(true);
                                    try {
                                        await axios.patch(
                                            `${API}/api/prospects/${rappelProspect.id}/status?statut=a_rappeler`,
                                            {},
                                            { withCredentials: true }
                                        );
                                        await axios.post(`${API}/api/taches`, {
                                        client_nom: rappelProspect.client_nom,
                                        titre: 'Prospect à rappeler',
                                        personne_concernee: 'Assistante',
                                        remarque: rappelRemarque,
                                        statut: 'a_traiter',
                                        linked_prospect_id: rappelProspect.id,
                                        type_rappel: 'prospect_rappeler',
                                    }, { withCredentials: true });
                                    toast.success('Statut mis à jour — tâche créée');
                                    setShowRappelModal(false);
                                    setRappelProspect(null);
                                    fetchProspects();
                                    fetchRappelStats();
                                    refreshActiveRappelList();
                                } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Erreur');
                                    } finally { setRappelSaving(false); }
                                }}
                            >
                                {rappelSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Confirmer
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Listing en attente */}
            {showListingModal && listingProspect && (
                <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="listing-modal">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="px-5 py-3 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Listing en attente — {listingProspect.client_nom}
                            </h2>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-500">Précisez comment le client compte envoyer sa liste afin que l'assistante puisse faire le suivi.</p>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Remarque sur le listing attendu</label>
                                <textarea
                                    value={listingRemarque}
                                    onChange={e => setListingRemarque(e.target.value)}
                                    rows={3}
                                    className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2e7cd6] focus:border-transparent resize-none"
                                    placeholder="Ex : Le client envoie la liste par email d'ici demain soir."
                                    autoFocus
                                    data-testid="listing-remarque"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
                            <Button variant="outline" size="sm" onClick={() => { setShowListingModal(false); setListingProspect(null); }}>Annuler</Button>
                            <Button
                                size="sm"
                                className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                disabled={listingSaving}
                                data-testid="listing-confirm-btn"
                                onClick={async () => {
                                    setListingSaving(true);
                                    try {
                                        await axios.patch(
                                            `${API}/api/prospects/${listingProspect.id}/status?statut=listing_en_attente`,
                                            {},
                                            { withCredentials: true }
                                        );
                                        await axios.post(`${API}/api/taches`, {
                                            client_nom: listingProspect.client_nom,
                                            titre: 'Listing en attente',
                                            personne_concernee: 'Assistante',
                                            remarque: listingRemarque,
                                            statut: 'a_traiter',
                                            linked_prospect_id: listingProspect.id,
                                            type_rappel: 'prospect_listing_attente',
                                        }, { withCredentials: true });
                                        toast.success('Statut mis à jour — tâche créée');
                                        setShowListingModal(false);
                                        setListingProspect(null);
                                        fetchProspects();
                                        fetchRappelStats();
                                        refreshActiveRappelList();
                                    } catch (error) {
                                        toast.error(error.response?.data?.detail || 'Erreur');
                                    } finally { setListingSaving(false); }
                                }}
                            >
                                {listingSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                                Confirmer
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            </ReadOnlyGuard>
        </div>
    );
}
